import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { rovingTabIndex, useRovingRadioGroup } from '../components/rovingFocus';
import { ModalOverlay } from '../components/ModalOverlay';
import { useModalA11y } from '../components/useModalA11y';
import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  filterAstToText,
  parseFilterExpr,
  type ParseResult,
  type TaxonomyTag,
} from '@xphoto/shared';
import { useI18n, type MessageKey, type TFunction } from '../i18n';
import { fetchTaxonomy } from '../taxonomy/taxonomy-api';
import { filterExprToHuman } from './filter-expr-human';
import { ApiError } from './api';

/**
 * ÉDITEUR D'EXPRESSION AVANCÉE (modale). Calqué sur `AdvancedSearchModal` (vraie boîte de
 * dialogue : focus initial/restauré, `Échap` ferme, clic sur le fond ferme, le caller
 * l'enveloppe d'un `<Portal>`). Il permet de COMPOSER un filtre booléen dans le mini-langage
 * (`filter-expr.ts` de `@xphoto/shared`) avec :
 *  - un champ d'expression (valeur = `expr` courant) à VALIDATION LIVE (`parseFilterExpr`) :
 *    à vide → rien ; erreur → message amical mappé depuis la clé ; OK → ÉCHO en langage clair ;
 *  - une PALETTE de tags (recyclée d'`AdvancedSearchModal`) : chaque tag INSÈRE au curseur son
 *    token canonique (`tag:…`, guillemets gérés), précédé par défaut de l'opérateur d'enchaînement ;
 *  - un sélecteur d'opérateur d'enchaînement OU/ET/SAUF (a11y `aria-checked`) ;
 *  - une légende du vocabulaire ; « Appliquer » (inerte si l'expression ne parse pas),
 *    « Réinitialiser », « Annuler », et « Enregistrer sous… » (optionnel).
 *
 * Le mini-langage RESTE en français canonique dans le champ (c'est une SYNTAXE, pas de
 * l'i18n) ; SEULS la légende, l'écho et les messages d'erreur sont localisés.
 */

/** Mappe une clé d'erreur du parser (`parseFilterExpr`) vers sa clé i18n amicale. */
const ERROR_KEY: Record<string, MessageKey> = {
  empty: 'filterExpr.error.empty',
  unexpected_token: 'filterExpr.error.unexpectedToken',
  unexpected_eof: 'filterExpr.error.unexpectedEof',
  unclosed_paren: 'filterExpr.error.unclosedParen',
  unclosed_quote: 'filterExpr.error.unclosedQuote',
  invalid_rating: 'filterExpr.error.invalidRating',
  unknown_type: 'filterExpr.error.unknownType',
  unknown_status: 'filterExpr.error.unknownStatus',
  unknown_field: 'filterExpr.error.unknownField',
  empty_value: 'filterExpr.error.emptyValue',
};

/** Opérateur d'enchaînement inséré avant un tag cliqué (mots CANONIQUES du mini-langage). */
type Connector = 'or' | 'and' | 'except';
const CONNECTOR_WORD: Record<Connector, string> = { or: 'OU', and: 'ET', except: 'SAUF' };
const CONNECTOR_LABEL_KEY: Record<Connector, MessageKey> = {
  or: 'filterExpr.connectorOr',
  and: 'filterExpr.connectorAnd',
  except: 'filterExpr.connectorExcept',
};

/** Repli insensible casse/accents (pour reconnaître un mot-clé opérateur en fin de champ). */
const fold = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const KEYWORDS = new Set([
  'ou',
  'or',
  'oder',
  'et',
  'and',
  'und',
  'sauf',
  'non',
  'not',
  'nicht',
  'ausser',
]);

/**
 * Le texte se termine-t-il par un point d'insertion « ouvert » (chaîne vide, parenthèse
 * ouvrante, ou opérateur) où AUCUN connecteur n'est nécessaire avant le prochain opérande ?
 */
function endsOpen(text: string): boolean {
  const trimmed = text.replace(/\s+$/, '');
  if (trimmed === '') return true;
  if (trimmed.endsWith('(')) return true;
  const lastWord = trimmed.split(/[\s()]+/).pop() ?? '';
  return KEYWORDS.has(fold(lastWord));
}

interface FilterExpressionModalProps {
  libraryId: string;
  /** Expression initiale (l'`expr` courant de la galerie). */
  initialExpr: string;
  /** Applique l'expression (texte canonique parsable) puis ferme. */
  onApply: (expr: string) => void;
  onClose: () => void;
  /**
   * « Enregistrer sous… » (optionnel) : remonte l'expression canonique courante pour
   * ouvrir la saisie nom + icône. Absent ⇒ le bouton n'est pas rendu.
   */
  onSaveAs?: (expr: string) => void;
}

export function FilterExpressionModal({
  libraryId,
  initialExpr,
  onApply,
  onClose,
  onSaveAs,
}: FilterExpressionModalProps): React.JSX.Element {
  const { t } = useI18n();
  const [expr, setExpr] = useState(initialExpr);
  const [connector, setConnector] = useState<Connector>('or');
  // Choix local d'opérateur (n'insère rien par lui-même) : la sélection SUIT le focus.
  const onConnectorKeyDown = useRovingRadioGroup('auto');
  const [search, setSearch] = useState('');
  const [tags, setTags] = useState<TaxonomyTag[] | null>(null);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Position de caret à replacer APRÈS une insertion (le textarea est contrôlé).
  const caretRef = useRef<number | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y({ dialogRef, initialFocusRef: textareaRef, onEscape: onClose });

  // Replace le caret après une insertion de token (le textarea est contrôlé → il faut le
  // repositionner à la main une fois la nouvelle valeur rendue).
  useLayoutEffect(() => {
    if (caretRef.current !== null && textareaRef.current) {
      const pos = caretRef.current;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(pos, pos);
      caretRef.current = null;
    }
  }, [expr]);

  // Charge le vocabulaire au montage (annulable au démontage) — recyclé d'`AdvancedSearchModal`.
  useEffect(() => {
    const ctrl = new AbortController();
    void (async (): Promise<void> => {
      try {
        const result = await fetchTaxonomy(libraryId, ctrl.signal);
        setTags(result.tags);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setLoadError(
          err instanceof ApiError && err.status === 403
            ? t('bulkTag.errorForbidden')
            : t('bulkTag.loadError'),
        );
        setTags([]);
      }
    })();
    return () => ctrl.abort();
  }, [libraryId, t]);

  const normalized = search.trim().toLocaleLowerCase();
  const filtered = useMemo(() => {
    if (tags === null) return [];
    if (normalized === '') return tags;
    return tags.filter((tag) =>
      tag.label.toLocaleLowerCase().normalize('NFD').includes(normalized.normalize('NFD')),
    );
  }, [tags, normalized]);

  // Validation LIVE : à vide → aucun retour ; sinon parse → erreur amicale OU écho.
  const parsed: ParseResult | null = expr.trim() === '' ? null : parseFilterExpr(expr);
  const errorMessage =
    parsed && !parsed.ok ? errorText(parsed.error, parsed.position, t) : undefined;
  const echo = parsed && parsed.ok ? filterExprToHuman(parsed.ast, t) : undefined;
  const canApply = parsed !== null && parsed.ok;

  const insertTag = (label: string): void => {
    const token = filterAstToText({ kind: 'tag', value: label });
    const el = textareaRef.current;
    const selStart = el?.selectionStart ?? expr.length;
    const selEnd = el?.selectionEnd ?? expr.length;
    const before = expr.slice(0, selStart);
    const after = expr.slice(selEnd);
    const needsConnector = before.trim() !== '' && !endsOpen(before);
    const leading = before === '' || /[\s(]$/.test(before) ? '' : ' ';
    const connectorText = needsConnector ? `${CONNECTOR_WORD[connector]} ` : '';
    const insertion = `${leading}${connectorText}${token}`;
    const next = before + insertion + after;
    caretRef.current = (before + insertion).length;
    setExpr(next);
  };

  const apply = (): void => {
    // On applique le texte CANONIQUE (re-sérialisé depuis l'AST) : URL propre + garantie
    // de re-parsing + correspondance fiable avec les filtres enregistrés.
    if (parsed && parsed.ok) onApply(filterAstToText(parsed.ast));
  };

  const loading = tags === null;
  const hasTags = tags !== null && tags.length > 0;

  return (
    <ModalOverlay onDismiss={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('filterExpr.editTitle')}
        className="flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
            <AdjustmentsHorizontalIcon className="h-6 w-6 text-muted" aria-hidden="true" />
            {t('filterExpr.editTitle')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            title={t('filterExpr.cancel')}
            aria-label={t('filterExpr.cancel')}
            className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto p-4 text-sm text-fg">
          <p className="text-muted">{t('filterExpr.intro')}</p>

          {/* Champ d'expression (mini-langage FR canonique). */}
          <section className="flex flex-col gap-2">
            <label htmlFor="filter-expr-field" className="text-sm font-semibold text-fg">
              {t('filterExpr.fieldLabel')}
            </label>
            <textarea
              id="filter-expr-field"
              ref={textareaRef}
              value={expr}
              onChange={(e) => setExpr(e.target.value)}
              rows={3}
              spellCheck={false}
              placeholder={t('filterExpr.placeholder')}
              aria-invalid={errorMessage !== undefined}
              aria-describedby="filter-expr-feedback"
              className="w-full resize-y rounded-md border border-border bg-surface px-3 py-2 font-mono text-sm text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            />

            {/* Retour de validation : erreur amicale OU écho en langage naturel. */}
            <div id="filter-expr-feedback" aria-live="polite" className="min-h-5">
              {errorMessage ? (
                <p role="alert" className="flex items-start gap-2 text-danger">
                  <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                  <span>{errorMessage}</span>
                </p>
              ) : echo ? (
                <p className="flex items-start gap-2 text-fg">
                  <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
                  <span>
                    <span className="text-muted">{t('filterExpr.echoLabel')} </span>
                    {echo}
                  </span>
                </p>
              ) : (
                <p className="text-muted">{t('filterExpr.empty')}</p>
              )}
            </div>
          </section>

          {/* Palette de tags : chaque tag INSÈRE son token canonique au curseur (clic, pas glisser). */}
          <section className="flex min-h-0 flex-col gap-2">
            <h3 className="text-sm font-semibold text-fg">{t('filterExpr.paletteLabel')}</h3>

            {/* Sélecteur d'opérateur d'enchaînement (OU/ET/SAUF). */}
            <div
              role="radiogroup"
              aria-label={t('filterExpr.connector')}
              onKeyDown={onConnectorKeyDown}
              className="inline-flex w-fit rounded-md border border-border p-0.5"
            >
              {(['or', 'and', 'except'] as const).map((mode) => {
                const active = connector === mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    tabIndex={rovingTabIndex(active)}
                    onClick={() => setConnector(mode)}
                    className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-xs font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${
                      active ? 'bg-brand text-brand-fg' : 'text-muted hover:text-fg'
                    }`}
                  >
                    {active && <CheckIcon className="h-4 w-4" aria-hidden="true" />}
                    {t(CONNECTOR_LABEL_KEY[mode])}
                  </button>
                );
              })}
            </div>

            {/* Recherche client par nom de tag. */}
            <div className="relative">
              <MagnifyingGlassIcon
                className="pointer-events-none absolute start-2 top-1/2 h-5 w-5 -translate-y-1/2 text-muted"
                aria-hidden="true"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={!hasTags}
                aria-label={t('filterExpr.paletteSearchLabel')}
                placeholder={t('filterExpr.paletteSearchPlaceholder')}
                className="h-9 w-full rounded-md border border-border-strong bg-surface ps-9 pe-3 text-sm text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
              />
            </div>

            {/* Liste des tags cliquables (insertion au curseur). */}
            <div
              className="max-h-48 min-h-0 overflow-y-auto"
              role="group"
              aria-label={t('filterExpr.paletteLabel')}
            >
              {loading ? (
                <p className="flex items-center gap-2 py-4 text-muted" role="status">
                  <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />
                  {t('bulkTag.loading')}
                </p>
              ) : !hasTags ? (
                <p className="py-4 text-muted">{t('bulkTag.empty')}</p>
              ) : filtered.length === 0 ? (
                <p className="py-4 text-muted">{t('bulkTag.noMatch')}</p>
              ) : (
                <ul className="flex flex-wrap gap-1.5">
                  {filtered.map((tag) => (
                    <li key={tag.id}>
                      <button
                        type="button"
                        onClick={() => insertTag(tag.label)}
                        className="inline-flex items-center gap-1 rounded-md border border-border bg-surface-2 px-2 py-1 text-sm text-fg hover:border-brand hover:text-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                      >
                        {tag.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {loadError && (
              <p role="alert" className="flex items-start gap-2 text-danger">
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <span>{loadError}</span>
              </p>
            )}
          </section>

          {/* Légende du vocabulaire (syntaxe canonique FR — non i18n). */}
          <section className="flex flex-col gap-1 rounded-md border border-border bg-surface-2 p-3">
            <h3 className="text-xs font-semibold text-fg">{t('filterExpr.legendTitle')}</h3>
            <p className="font-mono text-xs text-muted">{t('filterExpr.legend')}</p>
          </section>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border p-4">
          <button
            type="button"
            onClick={() => setExpr('')}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {t('filterExpr.reset')}
          </button>
          <div className="flex flex-wrap items-center gap-2">
            {onSaveAs && (
              <button
                type="button"
                onClick={() => {
                  if (parsed && parsed.ok) onSaveAs(filterAstToText(parsed.ast));
                }}
                disabled={!canApply}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
              >
                {t('filterExpr.saveAs')}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              {t('filterExpr.cancel')}
            </button>
            <button
              type="button"
              onClick={apply}
              disabled={!canApply}
              className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
            >
              {t('filterExpr.apply')}
            </button>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}

/** Message d'erreur amical d'un parsing raté (clé mappée + position 1-based quand fournie). */
function errorText(code: string, position: number | undefined, t: TFunction): string {
  const key = ERROR_KEY[code] ?? 'filterExpr.error.unexpectedToken';
  const template = t(key);
  // La position n'est interpolée que si le gabarit la référence (`{position}`).
  return position === undefined
    ? template
    : template.replace(/\{position\}/g, String(position + 1));
}
